/**
 * Read a picked file into base64 for the import calls.
 *
 * Chunked rather than `String.fromCharCode(...bytes)` in one go: a 700 KB
 * workbook is 700,000 arguments, which blows the call stack. The chunk size is
 * comfortably under every engine's argument limit.
 */
export async function readFileAsBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const CHUNK = 0x8000;

  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + CHUNK));
  }

  return btoa(binary);
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const ACCEPTED = ['.xlsx', '.xlsb', '.xls', '.csv'];

export const FILE_ACCEPT_ATTRIBUTE = ACCEPTED.join(',');

/** Reject obviously wrong files before a round trip (SCOPE §2.2-7). */
export function rejectionReason(file: File): string | null {
  const name = file.name.toLowerCase();
  if (!ACCEPTED.some((extension) => name.endsWith(extension))) {
    return `${file.name} is not a spreadsheet. Vendor order files are .xlsx, .xlsb or .csv.`;
  }
  if (file.size === 0) return `${file.name} is empty.`;
  if (file.size > 25 * 1024 * 1024) return `${file.name} is larger than 25 MB.`;
  return null;
}
